import { CreateTokenResponse, User } from "./types";
import React, { useEffect, useState } from "react";
import { login } from "./user-actions";
import {
  CLIENT_SCRIPT_ID,
  clientParams,
  elementScriptUrl,
  iframeUrl,
} from "./poker-client-launch";
import { OperatorEvent, OperatorEventType } from "./PokerDesktopComponent";
import { Modal, ModalBody } from "react-bootstrap";
import ModalHeader from "react-bootstrap/ModalHeader";
import logo from "./cubeia-logo.png";

export interface Props {
  user: User;
  logOut: () => void;
}

const addWebComponent = (
  container: HTMLDivElement | null,
  user: User,
  token: string,
) => {
  if (container) {
    const webComponentUrl = elementScriptUrl(user);
    const params = clientParams(user);
    container.innerHTML = "";
    const el = document.createElement("poker-client");
    el.setAttribute("id", "pokerClient");
    if (token.length > 0) {
      el.setAttribute("token", token);
    }
    // bootApp reads featureFlags and the game lobby launch from the element's
    // params attribute, which takes precedence over the script URL query string.
    // Always set it, from the same source as the script URL, so the two agree.
    el.setAttribute("params", params.toString());
    if (user.gameLobby) {
      // The dedicated attributes are the documented element API and win over
      // params inside bootApp; same source, so they cannot disagree. The element
      // spells the id in kebab case, like its other attributes.
      el.setAttribute("lobby", "game");
      el.setAttribute("game-id", params.get("gameId") ?? "");
    }
    container.appendChild(el);
    console.log("Loading web component script", webComponentUrl);
    // The element module can only be evaluated once per document: a second
    // evaluation throws on customElements.define. The script URL carries the
    // launch options, so changing them needs a page reload, not a new script.
    if (!document.querySelector(`#${CLIENT_SCRIPT_ID}`)) {
      const script = document.createElement("script");
      script.src = webComponentUrl;
      script.id = CLIENT_SCRIPT_ID;
      script.type = "module";
      script.async = true;
      document.body.appendChild(script);
    }
  }
};

export const PokerComponent = (props: Props) => {
  const [launchUrl, setLaunchUrl] = useState("");
  const [showCashier, setShowCashier] = useState(false);
  const [webComponentToken, setWebComponentToken] = useState("");
  const [preload, setPreload] = useState(false);

  const generateToken = () => {
    setLaunchUrl("");
    setWebComponentToken("");
    login(props.user).then((data: CreateTokenResponse) => {
      if (props.user.webComponent) {
        setWebComponentToken(data.token);
      } else {
        setLaunchUrl(iframeUrl(props.user, data.token));
      }
    });
  };

  const pokerClientRef = React.useRef<HTMLDivElement>(null);

  // The bar takes its own height off the top, so the client has to give it back.
  const showLobbyBar = Boolean(props.user.loggedIn && props.user.gameLobby);

  useEffect(() => {
    if (!props.user.loggedIn) {
      return;
    }
    generateToken();
    document.querySelector("body")?.classList.add("poker");

    const handleCashierOpen = () => setShowCashier(true);
    const handleLogout = (reason: string | undefined) => {
      if (reason !== "explicit") {
        return;
      }
      // logOut clears the session and reloads, which is what actually resets the
      // client: its element module is evaluated once per document.
      props.logOut();
    };

    // Iframe mode: the poker client (PostMessageSenderApi) posts window messages.
    const messageListener = (e: MessageEvent<OperatorEvent>) => {
      console.log("Event from poker", e);
      if (e.data.action === OperatorEventType.cashierOpenRequested) {
        handleCashierOpen();
      } else if (e.data.action === OperatorEventType.logout) {
        handleLogout(e.data.reason);
      }
    };
    window.addEventListener("message", messageListener);

    // Web component mode: the poker client (ClientFunctionSenderApi) calls
    // methods on window.PokerApi instead of posting window messages.
    if (props.user.webComponent) {
      (window as any).PokerApi = {
        cashierOpenRequested: () => handleCashierOpen(),
        logout: (_url: string | null, reason: string) => handleLogout(reason),
      };
    }

    return () => {
      window.removeEventListener("message", messageListener);
      document.querySelector("body")?.classList.remove("poker");
      if (props.user.webComponent) {
        delete (window as any).PokerApi;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user.loggedIn]);

  useEffect(() => {
    if (
      props.user.loggedIn &&
      props.user.webComponent &&
      props.user.operatorId &&
      !preload &&
      webComponentToken
    ) {
      addWebComponent(pokerClientRef.current, props.user, webComponentToken);
    }
  }, [props.user, webComponentToken, preload]);

  useEffect(() => {
    if (props.user.loggedIn) {
      return;
    }

    if (props.user.webComponent && props.user.operatorId) {
      setPreload(true);
      addWebComponent(pokerClientRef.current, props.user, webComponentToken);
    }
  }, [props.user, webComponentToken]);

  useEffect(() => {
    if (webComponentToken.length > 0) {
      (window as any).Poker?.login?.(webComponentToken);
    }
  }, [webComponentToken]);

  return (
    <>
      {showCashier && (
        <Modal show={true} size={"lg"}>
          <ModalHeader onHide={() => setShowCashier(false)} closeButton>
            Cashier
          </ModalHeader>
          <ModalBody>
            <div className={"alert alert-info"}>
              As an operator you would display your cashier here.
            </div>
          </ModalBody>
        </Modal>
      )}
      {showLobbyBar && (
        // The operator's own chrome around the game lobby. The lobby has no exit
        // of its own and only the classic client sends the logout event, so
        // without this bar the lobby is a dead end.
        <header className={"lobby-bar bg-dark text-white"}>
          <img src={logo} className={"App-logo"} alt={""} />
          <div className={"lobby-bar-actions"}>
            <span>{props.user.name}</span>
            <button
              type="button"
              className={"btn btn-primary btn-sm"}
              onClick={() => props.logOut()}
            >
              Close lobby
            </button>
          </div>
        </header>
      )}
      {props.user.webComponent && (
        <div
          ref={pokerClientRef}
          className={
            showLobbyBar
              ? "poker-client-container lobby-offset"
              : "poker-client-container"
          }
          style={{ display: props.user.loggedIn ? "block" : "none" }}
        />
      )}
      {!props.user.webComponent && props.user.loggedIn && (
        <iframe
          title={"poker"}
          className={showLobbyBar ? "poker-iframe lobby-offset" : "poker-iframe"}
          frameBorder={0}
          src={launchUrl}
          allowFullScreen={true}
          allow={"clipboard-write"}
        ></iframe>
      )}
    </>
  );
};
